import type { Optional } from "@suse-o11y/optional";
import { z } from "zod";

import type { TimeSeriesChart as OpenApiTimeSeriesChart } from "~/main/common/openapi";

import type { Settings } from "../../../components/widget-form/time-series-chart/settings";

import { Legend, ThresholdsMode, ThresholdsSteps, YAxis } from "./_sub-schemas";

const schema = z
	.object({
		yAxis: YAxis.schema.optional().catch(undefined),

		legend: Legend.schema.optional().catch(undefined),

		thresholds: z
			.object({
				mode: ThresholdsMode.schema.optional().catch(undefined),
				steps: ThresholdsSteps.schema,
			})
			.optional()
			.catch(undefined),

		visual: z
			.object({
				connectNulls: z.boolean().optional().catch(undefined),
			})
			.optional()
			.catch(undefined),
	})
	.catch({});

type TimeSeriesChart = z.infer<typeof schema>;

function fromSettings(settings: undefined | Settings): TimeSeriesChart {
	return {
		visual: {
			connectNulls: settings?.connectNulls,
		},

		legend: Legend.fromSettings(settings?.legend),

		yAxis: {
			show: settings?.yAxis?.show,
			label: settings?.yAxis?.label,
			min: settings?.min,
			max: settings?.max,
			format: {
				unit: settings?.unit,
				decimalPlaces: settings?.decimals,
			},
		},

		thresholds: {
			mode: settings?.thresholds?.mode,
			steps: ThresholdsSteps.fromSettings(settings?.thresholds?.steps),
		},
	};
}
function toSettings(spec: undefined | TimeSeriesChart): Settings {
	return {
		connectNulls: spec?.visual?.connectNulls,

		unit: spec?.yAxis?.format?.unit,
		decimals: spec?.yAxis?.format?.decimalPlaces,
		min: spec?.yAxis?.min,
		max: spec?.yAxis?.max,

		yAxis: {
			show: spec?.yAxis?.show,
			label: spec?.yAxis?.label,
		},

		legend: Legend.toSettings(spec?.legend),

		thresholds: {
			mode: spec?.thresholds?.mode,
			steps: spec?.thresholds?.steps,
		},
	} satisfies Optional<Settings>;
}

function openApiToSettings(chart: OpenApiTimeSeriesChart): Settings {
	const { unit, decimals, min, max, thresholds: ct, connectNulls } = chart;
	return {
		unit,
		decimals,
		min,
		max,
		connectNulls,

		thresholds: ct
			? {
					mode: ct.mode as "absolute" | "percent" | undefined,
					steps: ct.steps.map((step) => ({
						value: step.value,
						color: step.color ?? ct.defaultColor,
					})),
				}
			: undefined,
	};
}

const TimeSeriesChart = { schema, fromSettings, toSettings, openApiToSettings };

export { TimeSeriesChart };
